import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type Resources from "../i18n/resources";

type CountryNameKey = keyof Resources["common"]["countries"];

// Marketplace codes (plus US/GB aliases used by advertising profile IDs) → country-name keys in common.json.
export const MARKETPLACE_TO_COUNTRY_NAME = {
  USA: "United States",
  US: "United States",
  CA: "Canada",
  MX: "Mexico",
  BR: "Brazil",
  UK: "United Kingdom",
  GB: "United Kingdom",
  DE: "Germany",
  FR: "France",
  IT: "Italy",
  ES: "Spain",
  NL: "Netherlands",
  SE: "Sweden",
  PL: "Poland",
  BE: "Belgium",
  AE: "UAE",
  SA: "Saudi Arabia",
  IE: "Ireland",
  IN: "India",
  TR: "Turkey",
  AU: "Australia",
  JP: "Japan",
  SG: "Singapore",
} as const satisfies Record<string, CountryNameKey>;

const CANONICAL_MARKETPLACE_CODES = [
  "USA",
  "CA",
  "MX",
  "BR",
  "UK",
  "DE",
  "FR",
  "IT",
  "ES",
  "NL",
  "SE",
  "PL",
  "BE",
  "AE",
  "SA",
  "IE",
  "IN",
  "TR",
  "AU",
  "JP",
  "SG",
] as const satisfies readonly (keyof typeof MARKETPLACE_TO_COUNTRY_NAME)[];

/**
 * Names a marketplace or country code: the app's name for a marketplace, else the region's name in the
 * current language, else the code itself.
 */
export function useMarketplaceLabel(): (code: string) => string {
  const { t, i18n } = useTranslation("common");
  const language = i18n.resolvedLanguage ?? "en";
  return useMemo(() => {
    const regionNames = new Intl.DisplayNames([language, "en"], { type: "region" });
    return (code: string) => {
      const countryName = MARKETPLACE_TO_COUNTRY_NAME[code as keyof typeof MARKETPLACE_TO_COUNTRY_NAME];
      // oxlint-disable-next-line typescript/no-unnecessary-condition
      if (countryName) return t(($) => $.common.countries[countryName]);
      try {
        return regionNames.of(code) ?? code;
      } catch {
        // Not a well-formed region code.
        return code;
      }
    };
  }, [t, language]);
}

export function useMarketplaceOptions(): { value: string; label: string }[] {
  const { t, i18n } = useTranslation("common");
  return useMemo(
    () =>
      CANONICAL_MARKETPLACE_CODES.map((value) => ({
        value,
        label: t(($) => $.common.countries[MARKETPLACE_TO_COUNTRY_NAME[value]]),
      })).sort((a, b) => a.label.localeCompare(b.label, i18n.language)),
    [t, i18n.language],
  );
}

export const PRODUCT_AMAZON_LINKS: Record<string, string> = {
  USA: "https://www.amazon.com/dp/",
  CA: "https://www.amazon.ca/dp/",
  MX: "https://www.amazon.com.mx/dp/",
  BR: "https://www.amazon.com.br/dp/",
  UK: "https://www.amazon.co.uk/dp/",
  AE: "https://www.amazon.ae/dp/",
  SA: "https://www.amazon.sa/dp/",
  AU: "https://www.amazon.com.au/dp/",
  ES: "https://www.amazon.es/dp/",
  DE: "https://www.amazon.de/dp/",
  NL: "https://www.amazon.nl/dp/",
  FR: "https://www.amazon.fr/dp/",
  IT: "https://www.amazon.it/dp/",
  IN: "https://www.amazon.in/dp/",
  JP: "https://www.amazon.co.jp/dp/",
  SG: "https://www.amazon.sg/dp/",
  SE: "https://www.amazon.se/dp/",
  PL: "https://www.amazon.pl/dp/",
  BE: "https://www.amazon.com.be/dp/",
  TR: "https://www.amazon.com.tr/dp/",
  IE: "https://www.amazon.ie/dp/",
};

export const MARKETPLACE_TO_STORE_PAGE_URL_PREFIX_LOOKUP = {
  USA: "https://www.amazon.com/sp?seller=",
  US: "https://www.amazon.com/sp?seller=",
  CA: "https://www.amazon.ca/sp?seller=",
  MX: "https://www.amazon.com.mx/sp?seller=",
  BR: "https://www.amazon.com.br/sp?seller=",
  UK: "https://www.amazon.co.uk/sp?seller=",
  DE: "https://www.amazon.de/sp?seller=",
  FR: "https://www.amazon.fr/sp?seller=",
  IT: "https://www.amazon.it/sp?seller=",
  ES: "https://www.amazon.es/sp?seller=",
  NL: "https://www.amazon.nl/sp?seller=",
  SE: "https://www.amazon.se/sp?seller=",
  PL: "https://www.amazon.pl/sp?seller=",
  BE: "https://www.amazon.com.be/sp?seller=",
  AU: "https://www.amazon.com.au/sp?seller=",
  IN: "https://www.amazon.in/sp?seller=",
  JP: "https://www.amazon.co.jp/sp?seller=",
  SG: "https://www.amazon.sg/sp?seller=",
  AE: "https://www.amazon.ae/sp?seller=",
  SA: "https://www.amazon.sa/sp?seller=",
  TR: "https://www.amazon.com.tr/sp?seller=",
  EG: "https://www.amazon.eg/sp?seller=",
  IE: "https://www.amazon.ie/sp?seller=",
};

export const REGION_MARKETPLACE_LIST_MAP = {
  NA: ["USA", "US", "CA", "MX", "BR"],
  EU: ["UK", "AE", "ES", "DE", "NL", "FR", "IT", "IN", "GR", "SE", "PL", "BE", "SA", "TR", "IE"],
  FE: ["AU", "JP", "SG"],
};

export const MARKETPLACE_CURRENCY_DENOMINATION: Record<string, string> = {
  USA: "$",
  CA: "$",
  MX: "$",
  BR: "R$",
  UK: "£",
  AE: "AED",
  SA: "﷼",
  AU: "$",
  ES: "€",
  DE: "€",
  NL: "€",
  SE: "kr",
  PL: "zł",
  BE: "€",
  IT: "€",
  FR: "€",
  IN: "₹",
  SG: "S$",
  JP: "¥",
  TR: "₺",
  IE: "€",
};

export const MARKETPLACE_CURRENCY_CODE: Record<string, string> = {
  USA: "USD",
  CA: "CAD",
  MX: "MXN",
  BR: "BRL",
  UK: "GBP",
  AE: "AED",
  SA: "SAR",
  AU: "AUD",
  ES: "EUR",
  DE: "EUR",
  SE: "SEK",
  PL: "PLN",
  BE: "EUR",
  NL: "EUR",
  IT: "EUR",
  FR: "EUR",
  IN: "INR",
  JP: "JPY",
  SG: "SGD",
  TR: "TRY",
  IE: "EUR",
};

export const MARKETPLACE_TIMEZONE = {
  CA: "US/Pacific",
  US: "US/Pacific",
  USA: "US/Pacific",
  UK: "Europe/London",
  AE: "Asia/Dubai",
  AU: "Australia/Sydney",
  IN: "Asia/Kolkata",
  BR: "America/Sao_Paulo",
  MX: "US/Pacific",
  ES: "Europe/Paris",
  NL: "Europe/Amsterdam",
  IT: "Europe/Paris",
  FR: "Europe/Paris",
  DE: "Europe/Paris",
  SG: "Asia/Singapore",
  JP: "Asia/Tokyo",
  PL: "Europe/Warsaw",
  SE: "Europe/Stockholm",
  BE: "Europe/Brussels",
  SA: "Asia/Riyadh",
  TR: "Asia/Istanbul",
  IE: "Europe/Dublin",
};

function toCurrency(data: unknown, marketPlace: string) {
  if (typeof data === "number") {
    const fixedValue = data.toFixed(2);
    const val = fixedValue !== "-" ? MARKETPLACE_CURRENCY_DENOMINATION[marketPlace] + fixedValue : fixedValue;

    return toComma(val);
  }
}

function toComma(val: { toString: () => string }) {
  return val.toString().replaceAll(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export { toCurrency };
